import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Chessboard, ChessboardOptions } from 'react-chessboard';
import { Chess, Square } from 'chess.js';
import { useSettings } from '../context/SettingsContext';

interface ChessBoardProps {
    fen: string;
    onMove?: (move: { from: string; to: string; promotion?: string }) => void;
    orientation?: 'white' | 'black';
    draggable?: boolean;
    customArrows?: [string, string, string][];
    customSquareStyles?: Record<string, React.CSSProperties>;
    onSquareClick?: (square: string) => void;
}

const ChessBoard: React.FC<ChessBoardProps> = React.memo(({
    fen,
    onMove,
    orientation = 'white',
    draggable = true,
    customArrows = [],
    customSquareStyles = {},
    onSquareClick: onSquareClickProp
}) => {
    const { settings } = useSettings();
    const [boardWidth, setBoardWidth] = useState(400);
    const containerRef = useRef<HTMLDivElement>(null);

    // Initial game state for validation
    const chessRef = useRef(new Chess(fen === 'start' || !fen ? undefined : fen));

    // States for click-to-move and legal move indicators
    const [moveFrom, setMoveFrom] = useState<Square | null>(null);
    const [optionSquares, setOptionSquares] = useState<Record<string, React.CSSProperties>>({});

    useEffect(() => {
        try {
            chessRef.current = new Chess(fen === 'start' || !fen ? undefined : fen);
            setMoveFrom(null);
            setOptionSquares({});
        } catch (e) {
            chessRef.current = new Chess();
        }
    }, [fen]);

    useEffect(() => {
        if (!containerRef.current) return;
        const updateWidth = () => {
            if (containerRef.current) {
                const { width, height } = containerRef.current.getBoundingClientRect();
                const size = Math.min(width, height) - 20;
                setBoardWidth(Math.max(size, 200));
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        const observer = new ResizeObserver(updateWidth);
        observer.observe(containerRef.current);
        return () => {
            window.removeEventListener('resize', updateWidth);
            observer.disconnect();
        };
    }, []);

    const getMoveOptions = useCallback((square: Square): boolean => {
        const moves = chessRef.current.moves({ square, verbose: true });
        if (moves.length === 0) {
            setOptionSquares({});
            return false;
        }
        const squares: Record<string, React.CSSProperties> = {};
        moves.forEach((move) => {
            const isCapture = !!chessRef.current.get(move.to as Square);
            squares[move.to] = {
                background: isCapture
                    ? 'radial-gradient(circle, rgba(0,0,0,.15) 85%, transparent 85%)'
                    : 'radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)',
                borderRadius: '50%',
                cursor: 'pointer',
            };
        });
        squares[square] = { background: 'rgba(255, 255, 0, 0.25)', cursor: 'pointer' };
        setOptionSquares(squares);
        return true;
    }, []);

    const arrows = useMemo(() =>
        customArrows.map(([start, end, color]) => ({
            startSquare: start as string,
            endSquare: end as string,
            color: color || 'rgba(99, 102, 241, 0.6)',
        })),
        [customArrows]
    );

    const mergedSquareStyles = useMemo(() => ({
        ...customSquareStyles,
        ...optionSquares,
    }), [customSquareStyles, optionSquares]);

    const handlePieceDrop = (sourceSquare: string, targetSquare: string): boolean => {
        if (!draggable || !onMove || !targetSquare) return false;

        try {
            const move = {
                from: sourceSquare as Square,
                to: targetSquare as Square,
                promotion: 'q'
            };

            // Validate move locally first
            const test = new Chess(chessRef.current.fen());
            const result = test.move(move);

            if (result) {
                onMove(move);
                setMoveFrom(null);
                setOptionSquares({});
                return true;
            }
        } catch (e) {
            return false;
        }
        return false;
    };

    const handleInternalSquareClick = (square: string) => {
        if (onSquareClickProp) {
            onSquareClickProp(square);
            return;
        }
        if (!draggable || !onMove) return;

        const sq = square as Square;

        // If we haven't selected a piece yet
        if (!moveFrom) {
            const hasMoves = getMoveOptions(sq);
            if (hasMoves) setMoveFrom(sq);
            return;
        }

        // If we already have a piece selected, try to move it
        const isMoveSqr = sq in optionSquares && sq !== moveFrom;
        if (isMoveSqr) {
            onMove({ from: moveFrom, to: sq, promotion: 'q' });
            setMoveFrom(null);
            setOptionSquares({});
        } else {
            // Select a different piece or deselect
            const hasMoves = getMoveOptions(sq);
            if (hasMoves) {
                setMoveFrom(sq);
            } else {
                setMoveFrom(null);
                setOptionSquares({});
            }
        }
    };

    const options: ChessboardOptions = {
        id: 'BasicBoard',
        position: fen,
        boardOrientation: orientation,
        boardStyle: { width: boardWidth, height: boardWidth },
        allowDragging: draggable,
        animationDurationInMs: 200,
        darkSquareStyle: { backgroundColor: settings.boardTheme.dark },
        lightSquareStyle: { backgroundColor: settings.boardTheme.light },
        arrows: arrows as any,
        squareStyles: mergedSquareStyles,
        onPieceDrop: (args: any) => handlePieceDrop(args.sourceSquare, args.targetSquare),
        onSquareClick: (args: any) => handleInternalSquareClick(args.square)
    };

    return (
        <div ref={containerRef} className="w-full h-full flex items-center justify-center p-2 select-none overflow-hidden bg-[#1a1c24]">
            <Chessboard options={options} />
        </div>
    );
});

export default ChessBoard;
